import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Course } from '@lx/shared/data-access/types';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { CourseState, CourseStore, QuoteToolLiteStore } from '../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { SelectableField } from '@lx/quote-tool/lite/shared/types';
import { JuniorsCourseUpgradePopupComponent } from '../juniors-course-upgrade-popup/juniors-course-upgrade-popup.component';
import { SingleSelectionButtonListFieldComponent } from '../single-selection-button-list-field/single-selection-button-list-field.component';
import { CourseClassificationEnum } from '@lx/quote-tool/shared/types';

@Component({
  standalone: true,
  selector: 'lx-juniors-course-selector-field',
  templateUrl: './juniors-course-selector-field.component.html',
  styleUrls: ['./juniors-course-selector-field.component.scss'],
  imports: [SingleSelectionButtonListFieldComponent, JuniorsCourseUpgradePopupComponent, AsyncPipe, FormsModule],
})
export class JuniorsCourseSelectorFieldComponent implements OnChanges {
  @Input() courses: Course[] | undefined;

  @Output() selectedCourseChanged = new EventEmitter<SelectableField<CourseDetails>>();

  courses$: Observable<Course[]> | undefined;
  courseSelectorDisabled = true;
  selectedCourse: CourseDetails | undefined;
  juniorCourseUpgradePopup = false;

  lessons = ' ' + $localize`lessons/week`;
  standard = $localize`Standard`;
  intensive = $localize`Intensive`;

  coursesPlaceholder = [
    { name: this.standard, code: undefined, description: '20' + this.lessons },
    { name: this.intensive, code: undefined, description: '25' + this.lessons },
  ];

  private readonly courseStore = inject(CourseStore);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly destroyRef = inject(DestroyRef);

  ngOnChanges({ courses }: SimpleChanges): void {
    if (courses.currentValue) {
      this.courseStore.selectedSchoolLocationCourseAndLanguage$
        .pipe(
          switchMap(({ selectedCourse, selectedLanguage }: CourseState) => combineLatest([of(this.courses), of(selectedCourse), of(selectedLanguage)])),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(([courses, selectedCourse, selectedLanguage]: [Course[], CourseDetails, string]) => {
          this.prepareComponentData(courses, selectedCourse, selectedLanguage);
        });
    }
  }

  setSelectedCourse(selectedCourseCode: string) {
    this.courses$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((courses: Course[]) => {
      const selectedCourse = courses?.find((course: { code: string }) => course?.code === selectedCourseCode);
      if (selectedCourse?.courseClassification?.toLocaleLowerCase() === CourseClassificationEnum.standard) {
        this.quoteToolLiteStore.juniorCourseUpgrade$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((juniorCourseUpgrade) => {
          if (juniorCourseUpgrade) {
            return;
          }
          this.juniorCourseUpgradePopup = true;
          this.quoteToolLiteStore.updateState({ juniorCourseUpgrade: true });
        });
      }
      this.selectedCourse = this.buildCourseDetails(selectedCourse, selectedCourse?.type?.code) as CourseDetails;
      this.selectedCourseChanged.emit({ value: this.selectedCourse, fromUser: true });
    });
  }

  upgradeCourse(upgrade: boolean) {
    if (upgrade) {
      this.courses$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((courses: Course[]) => {
        const selectedCourse = courses?.find(
          (course) => course?.courseClassification?.toLocaleLowerCase() === CourseClassificationEnum.intensive,
        ) as CourseDetails;
        this.selectedCourse = this.buildCourseDetails(selectedCourse) as CourseDetails;
        this.selectedCourseChanged.emit({ value: this.selectedCourse, fromUser: true });
      });
    }
    this.quoteToolLiteStore.updateState({ upselling: upgrade });
    this.juniorCourseUpgradePopup = false;
  }

  private buildCourseDetails(selectedCourse: Course | CourseDetails, selectedCourseType?: string) {
    if (!selectedCourse) {
      return {};
    }

    const { code, name, durationMin, durationMax, courseDates, type, productPackage } = selectedCourse;
    return {
      code,
      name,
      type: selectedCourseType ? selectedCourseType : type,
      durationMin,
      durationMax,
      productPackage,
      courseDates,
    };
  }

  private prepareComponentData(courses: Course[], selectedCourse: CourseDetails, language: string) {
    const filteredCoursesByLanguage = courses
      ?.filter((course: Course) => course?.courseLanguage === language)
      .sort((course: Course) => (course?.courseClassification?.toLocaleLowerCase() === CourseClassificationEnum.standard ? -1 : 1))
      ?.map((course: Course) => ({
        ...course,
        description: course?.lesson?.numberOfLessons + this.lessons,
        name: course?.courseClassification?.toLocaleLowerCase() === CourseClassificationEnum.standard ? this.standard : this.intensive,
      }));
    this.courseSelectorDisabled = !filteredCoursesByLanguage?.length;
    this.courses$ = this.courseSelectorDisabled ? of(this.coursesPlaceholder) : of(filteredCoursesByLanguage);

    if (courses.length) {
      this.initializeSelectedCourse(filteredCoursesByLanguage, selectedCourse);
    }
  }

  private initializeSelectedCourse(courses: Course[], selectedCourseInStore: CourseDetails) {
    this.selectedCourse = courses?.find((course: Course) => {
      return course?.code === selectedCourseInStore?.code;
    }) as CourseDetails;

    if (!this.selectedCourse) {
      this.selectedCourseChanged.emit({ value: undefined });
    }
  }
}
